import axios from "axios";

/**
 * Token getter set synchronously during AuthTokenBridge render so the first
 * child effect (e.g. PermissionProvider) already has a Bearer interceptor.
 */
let getAccessTokenSilently = null;
let onSessionExpired = null;
let sessionExpiredInFlight = false;

const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

function isAuth0LoginRequiredError(err) {
  const code = String(err?.error || err?.code || "").toLowerCase();
  const message = String(err?.message || "").toLowerCase();
  return (
    code === "login_required" ||
    code === "consent_required" ||
    code === "missing_refresh_token" ||
    code === "invalid_grant" ||
    message.includes("login_required") ||
    message.includes("missing refresh token") ||
    message.includes("invalid_grant")
  );
}

function shouldIgnoreApiUnauthorized(error) {
  const message = String(error?.response?.data?.message || "");
  // Valid JWT but missing email claim — ops/config issue, not an expired session.
  return message.toLowerCase().includes("email claim missing");
}

/**
 * Deduped session-expired handler so parallel 401s open one dialog.
 */
export function triggerSessionExpired(reason) {
  if (sessionExpiredInFlight) return;
  if (typeof onSessionExpired !== "function") return;

  sessionExpiredInFlight = true;
  Promise.resolve()
    .then(() => onSessionExpired(reason))
    .catch((err) => {
      console.warn("Session-expired handler failed:", err?.message || err);
    })
    .finally(() => {
      // Allow a future expiry after the user signs in again.
      setTimeout(() => {
        sessionExpiredInFlight = false;
      }, 2000);
    });
}

export function setSessionExpiredHandler(handler) {
  onSessionExpired = handler || null;
  return () => {
    if (onSessionExpired === handler) {
      onSessionExpired = null;
    }
  };
}

// Register once at module load — never eject on remount (avoids StrictMode races).
axios.interceptors.request.use(async (config) => {
  const url = typeof config.url === "string" ? config.url : "";
  if (url.includes("/api/public/")) {
    return config;
  }

  const getter = getAccessTokenSilently;
  if (!getter) {
    return config;
  }

  try {
    const token = await getter({
      authorizationParams: audience ? { audience } : undefined,
    });
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (err) {
    console.warn(
      "Failed to get access token for API request:",
      err?.message || err,
    );
    if (isAuth0LoginRequiredError(err)) {
      triggerSessionExpired(err);
      return Promise.reject(err);
    }
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = String(error?.config?.url || "");
    const isPublic = url.includes("/api/public/");

    if (status === 401 && !isPublic && !shouldIgnoreApiUnauthorized(error)) {
      triggerSessionExpired(error);
    }

    return Promise.reject(error);
  },
);

/**
 * Point the shared interceptor at the current Auth0 token function.
 * Safe to call during render of AuthTokenBridge (before children mount).
 */
export function setAccessTokenGetter(getter) {
  getAccessTokenSilently = getter || null;
  return () => {
    if (getAccessTokenSilently === getter) {
      getAccessTokenSilently = null;
    }
  };
}

/** @deprecated Use setAccessTokenGetter — kept name for call-site clarity. */
export function setupAuthInterceptor(getter) {
  return setAccessTokenGetter(getter);
}
